import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * Bundle-version drift auto-recovery.  Runs ONCE per session on
 * app mount.  Compares the currently-running `main.<hash>.js`
 * filename (read off the DOM at boot) against what the server is
 * currently serving in a fresh `/index.html`.  If they differ the
 * user is on a stale bundle from a prior deploy — we surface a
 * toast, then force-reload with cache-bypass so they pick up the
 * new bundle without asking IT to "clear cache."
 *
 * This is a permanent fix for the "hard refresh doesn't help"
 * class of bugs (browser HTTP disk cache pinning old JS despite
 * `Cache-Control: no-cache` on the HTML).
 *
 * Zero-effect scenarios:
 *   • No `main.*.js` script tag found → nothing to compare, no-op.
 *   • Fetch fails (offline, prod down) → silently no-op.
 *   • Same hash → silently no-op.
 */
export function useBundleVersionCheck() {
  const ranRef = useRef(false);
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const runningHash = extractHashFromScriptTags(document);
    if (!runningHash) return;

    // Cache-bust the HTML fetch itself so we get the current
    // server-side response, not a stale cached copy of index.html.
    const url = `/index.html?_=${Date.now()}`;
    fetch(url, { cache: "no-store", credentials: "same-origin" })
      .then((r) => (r.ok ? r.text() : null))
      .then((html) => {
        if (!html) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const serverHash = extractHashFromScriptTags(doc);
        if (!serverHash || serverHash === runningHash) return;

        // Stale bundle in this tab — toast + force reload.  Reloads
        // with `location.reload()` (modern browsers ignore the
        // `true` cache-bypass arg but honor the cache-control
        // headers on the HTML response, which are no-store).
        // Timestamp query on the current URL guarantees a fresh
        // HTML fetch on reload.
        toast.info("Update available — reloading to apply…",
                    { duration: 1200 });
        setTimeout(() => {
          try {
            const u = new URL(window.location.href);
            u.searchParams.set("_v", String(Date.now()));
            window.location.replace(u.toString());
          } catch {
            window.location.reload();
          }
        }, 1000);
      })
      .catch(() => { /* offline or SSR — never abort the app */ });
  }, []);
}

function extractHashFromScriptTags(root) {
  const scripts = root.querySelectorAll('script[src*="/static/js/main."]');
  for (const s of scripts) {
    const m = (s.getAttribute("src") || "").match(/\/static\/js\/main\.([a-z0-9]+)\.js/i);
    if (m) return m[1];
  }
  return null;
}
